/* Illustrated hero boards (user artwork) auto-assigned per country geography.
   board1 arid+city · board2 tropical highland · board3 coastal · board4 urban
   board5 alpine · board6 desert · board7 river valley · board8 plains */

const TYPE_BOARD = { arid: 1, tropical: 2, coastal: 3, urban: 4, alpine: 5, desert: 6, river: 7, plains: 8 };

const COUNTRY_TYPE = {
  alpine: ["CH", "AT", "NP", "BT", "NO", "SE", "FI", "IS", "GL", "CA", "AD", "LI", "GE", "AM", "KG", "TJ", "CL", "AR", "NZ", "SI", "SK", "ME", "MK", "AL", "XK", "LS"],
  desert: ["AE", "SA", "QA", "KW", "BH", "OM", "EG", "LY", "DZ", "TN", "MA", "MR", "ML", "NE", "TD", "SD", "JO", "IQ", "IR", "SY", "YE", "IL", "PS", "TM", "UZ", "AF", "MN", "NA", "BW", "EH", "DJ", "ER", "SO"],
  coastal: ["MV", "FJ", "ID", "PH", "MY", "TH", "LK", "GR", "HR", "PT", "CY", "MT", "BS", "BB", "CU", "DO", "JM", "TT", "SC", "MU", "KM", "CV", "PF", "NC", "VU", "WS", "TO", "KI", "FM", "MH", "PW", "SB", "TV", "NR", "AU", "BZ", "PA", "CR", "AG", "LC", "VC", "GD", "KN", "DM", "HT", "TL", "BN", "SG", "AW", "CW", "BM", "KY", "TC", "VG", "GU", "MP", "AS", "CK", "IE"],
  tropical: ["BR", "CO", "VE", "PE", "EC", "BO", "KE", "TZ", "UG", "RW", "BI", "GT", "HN", "NI", "SV", "LA", "VN", "MM", "KH", "ET", "CM", "CD", "CG", "GA", "GH", "CI", "NG", "LR", "SL", "GN", "GQ", "ST", "MG", "MZ", "MW", "ZM", "ZW", "AO", "PG", "GY", "SR", "GF", "PR"],
  urban: ["US", "GB", "DE", "FR", "JP", "KR", "CN", "HK", "TW", "NL", "BE", "LU", "DK", "IT", "ES", "MC", "SM", "MO"],
  arid: ["IN", "MX", "TR", "ZA", "PK", "AZ", "KZ", "SN", "GM", "BF", "TG", "BJ", "SZ"],
  river: ["RU", "UA", "PL", "HU", "RO", "BG", "CZ", "RS", "BA", "BY", "MD", "LT", "LV", "EE", "BD", "KP", "PY", "UY"],
};

const CODE_TO_TYPE = {};
for (const [t, codes] of Object.entries(COUNTRY_TYPE)) codes.forEach((c) => { CODE_TO_TYPE[c] = t; });

const NAME_TO_CODE = {
  "united states": "US", usa: "US", "united kingdom": "GB", canada: "CA", india: "IN",
  australia: "AU", germany: "DE", france: "FR", japan: "JP", china: "CN",
  "united arab emirates": "AE", brazil: "BR", mexico: "MX", russia: "RU", ireland: "IE",
  netherlands: "NL", spain: "ES", italy: "IT", turkey: "TR", singapore: "SG",
  "hong kong": "HK", qatar: "QA", "saudi arabia": "SA", switzerland: "CH",
  "south korea": "KR", "republic of korea": "KR", thailand: "TH", indonesia: "ID",
  malaysia: "MY", philippines: "PH", "new zealand": "NZ", norway: "NO", sweden: "SE",
  finland: "FI", denmark: "DK", portugal: "PT", greece: "GR", egypt: "EG",
  "south africa": "ZA", kenya: "KE", ethiopia: "ET", nigeria: "NG", chile: "CL",
  argentina: "AR", colombia: "CO", peru: "PE", vietnam: "VN", pakistan: "PK",
  bangladesh: "BD", "sri lanka": "LK", nepal: "NP", austria: "AT", belgium: "BE",
  poland: "PL", "czech republic": "CZ", hungary: "HU", romania: "RO", ukraine: "UA",
  israel: "IL", morocco: "MA", iceland: "IS", fiji: "FJ", maldives: "MV",
};

/* Dedicated AI-illustrated entity boards (2026-06): 4 airport + 4 airline themes. */
const LOWCOST_RX = /\b(ryanair|easyjet|spirit|frontier|indigo|air ?asia|wizz|vueling|jetstar|scoot|volaris|viva|pegasus|norwegian|flydubai|allegiant|southwest|akasa|spicejet|go ?first|lion air|cebu pacific|nok air|transavia|eurowings|jet2|breeze|zipair|peach|jeju|t'way|smartwings)\b/i;
const REGIONAL_RX = /\b(express|link|connect|regional|commuter|connection|shuttle|hop)\b/i;

function countryType(codeOrName) {
  let cc = (codeOrName || "").trim();
  if (cc.length !== 2) cc = NAME_TO_CODE[cc.toLowerCase()] || cc;
  return CODE_TO_TYPE[cc.toUpperCase()];
}

export function entityBoardFor(kind, entity = {}) {
  if (kind === "airline") {
    const n = entity.name || "";
    if (/cargo|freight/i.test(n)) return { src: "/img/hero-boards/al-cargo.webp", label: "Cargo Carrier" };
    if (LOWCOST_RX.test(n)) return { src: "/img/hero-boards/al-lowcost.webp", label: "Low-Cost Carrier" };
    if (REGIONAL_RX.test(n)) return { src: "/img/hero-boards/al-regional.webp", label: "Regional Carrier" };
    return { src: "/img/hero-boards/al-legacy.webp", label: "Legacy Carrier" };
  }
  const t = countryType(entity.country);
  if (entity.type === "large_airport") return { src: "/img/hero-boards/ap-hub.webp", label: "International Hub" };
  if (t === "coastal" || t === "tropical") return { src: "/img/hero-boards/ap-coastal.webp", label: "Coastal & Island Airport" };
  if (t === "alpine") return { src: "/img/hero-boards/ap-mountain.webp", label: "Mountain Airport" };
  return { src: "/img/hero-boards/ap-regional.webp", label: "Regional Airport" };
}

export function heroBoardFor(codeOrName) {
  let cc = (codeOrName || "").trim();
  if (cc.length !== 2) cc = NAME_TO_CODE[cc.toLowerCase()] || cc;
  cc = cc.toUpperCase();
  const type = CODE_TO_TYPE[cc];
  let n;
  if (type) n = TYPE_BOARD[type];
  else {
    let h = 0;
    for (const ch of cc) h = (h * 31 + ch.charCodeAt(0)) % 997;
    n = (h % 8) + 1;
  }
  return `/img/hero-boards/board${n}.webp`;
}
